// The game strip's layout (2026-10-10): the row spans the full width of the
// dashboard below it, so its tiles share that width evenly, as many whole
// tiles as fit at their narrowest; and its ‹ › overlay the row's ends,
// shown only where there is more to scroll to. Pure: the component measures,
// this decides.

/** The narrowest a tile may be, in rem (its date, result and stage on one line each). */
export const TILE_MIN_REM = 5.75;

/** How many whole tiles fit side by side in `width`, each at least `tileMin` wide with `gap` between them; at least one. */
export function stripColumns(width: number, tileMin: number, gap: number): number {
  if (!(width > 0) || !(tileMin > 0)) return 1;
  return Math.max(1, Math.floor((width + Math.max(0, gap)) / (tileMin + Math.max(0, gap))));
}

/** Whether the row can scroll further back (towards the newer games, left) and forward (the older, right); a pixel of rounding is no scroll. */
export function scrollEdges(scrollLeft: number, clientWidth: number, scrollWidth: number): { back: boolean; forward: boolean } {
  return { back: scrollLeft > 1, forward: scrollLeft + clientWidth < scrollWidth - 1 };
}

/** How far one page moves the row: the visible tiles and the gap after the last, so the next page starts on a tile's edge. */
export function pageDistance(clientWidth: number, gap: number): number {
  return clientWidth + Math.max(0, gap);
}

/**
 * What the strip keeps in view (2026-10-19): the selected game's id, not its
 * index. A game imported while Home is open goes first and, without `?game=`,
 * becomes the selection at the same index 0; the browser meanwhile re-snaps
 * the row (`scroll-snap-type`) to the tile it was snapped to, the previous
 * newest, which pushes the new tile out of view to the left. Keyed on the
 * id, the strip scrolls the new selection back into view; with an older game
 * selected the key stays, and the re-snap keeps that tile where it was.
 */
export function revealKey(ids: readonly string[], index: number): string | undefined {
  return index >= 0 ? ids[index] : undefined;
}

/**
 * How far to scroll the row so a tile is wholly in view, from their visible
 * edges (`getBoundingClientRect`): negative back towards the newer games, 0
 * when it already is. The row alone scrolls, never the page, so a game
 * imported while the reader is further down Home does not pull them up.
 */
export function revealDelta(tile: { left: number; right: number }, row: { left: number; right: number }): number {
  if (tile.left < row.left) return tile.left - row.left;
  if (tile.right > row.right) return tile.right - row.right;
  return 0;
}
